import { useState, useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert VAPID key from URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState('default');
  const registrationRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Check if push notifications are supported
  useEffect(() => {
    const checkSupport = () => {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);
      
      if (!supported) {
        setIsLoading(false);
        setError('Push notifications are not supported in this browser');
      }
    };
    
    checkSupport();
  }, []);

  // Initialize service worker and check existing subscription
  useEffect(() => {
    if (!isSupported) return;
    
    let isMounted = true;
    
    const initializeServiceWorker = async () => {
      try {
        // Register service worker
        const registration = await navigator.serviceWorker.register('/service-worker.js');
        
        // Wait for service worker to be ready
        await navigator.serviceWorker.ready;
        
        if (!isMounted) return;
        
        registrationRef.current = registration;
        setPermission(Notification.permission);
        
        // Check for existing subscription
        const existingSubscription = await registration.pushManager.getSubscription();
        
        if (isMounted) {
          if (existingSubscription) {
            subscriptionRef.current = existingSubscription;
            setIsSubscribed(true);
          } else {
            setIsSubscribed(false);
          }
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(`Service Worker initialization failed: ${err.message}`);
          setIsLoading(false);
        }
      }
    };
    
    initializeServiceWorker();
    
    return () => {
      isMounted = false;
    };
  }, [isSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!registrationRef.current) {
      setError('Service Worker not ready');
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Request notification permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);
      
      if (permissionResult !== 'granted') {
        setError('Notification permission was denied');
        setIsLoading(false);
        return false;
      }
      
      // Get VAPID public key from environment or backend
      let vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      
      if (!vapidPublicKey) {
        // Fetch from backend
        try {
          const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
          if (response.ok) {
            const data = await response.json();
            vapidPublicKey = data.publicKey;
          }
        } catch (e) {
          console.error('Failed to fetch VAPID key:', e);
        }
      }
      
      if (!vapidPublicKey) {
        setError('VAPID public key is not configured');
        setIsLoading(false);
        return false;
      }
      
      // Convert VAPID key and create subscription
      const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
      
      const newSubscription = await registrationRef.current.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedKey
      });
      
      // Send subscription to backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: newSubscription.endpoint,
          keys: newSubscription.toJSON().keys,
          user_agent: navigator.userAgent
        })
      });
      
      if (!response.ok) {
        throw new Error(`Backend subscription failed: ${response.statusText}`);
      }
      
      subscriptionRef.current = newSubscription;
      setIsSubscribed(true);
      
      return true;
    } catch (err) {
      setError(`Subscription failed: ${err.message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscriptionRef.current) {
      setError('No active subscription to unsubscribe');
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove subscription from backend
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: subscriptionRef.current.endpoint
        })
      });
      
      if (!response.ok) {
        throw new Error(`Backend unsubscription failed: ${response.statusText}`);
      }
      
      // Unsubscribe from push service
      await subscriptionRef.current.unsubscribe();
      
      subscriptionRef.current = null;
      setIsSubscribed(false);
      
      return true;
    } catch (err) {
      setError(`Unsubscription failed: ${err.message}`);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    isLoading,
    error,
    permission,
    subscribe,
    unsubscribe
  };
}

export default usePushNotifications;
